import { useState, ChangeEvent, FormEvent } from "react";
import { isFormValid, isValidEmail } from "../../utils/authHelpers";
import { useNavigate } from "react-router-dom";
import { getApiUrl } from "../../utils/api";
import devLogger from "../../utils/devLogger";

export const useUserRegister = (): UseUserSignUpReturn => {
  const navigate = useNavigate();

  const [signUpDetails, setSignUpDetails] = useState<SignUpDetailsProps>({
    email: undefined,
    password: undefined,
  });

  const [errors, setErrors] = useState<{ email?: string; password?: string }>({
    email: undefined,
    password: undefined,
  });

  const [loading, setLoading] = useState(false);

  const [signUpError, setSignUpError] = useState<SignUpError>({
    error: false,
    message: "",
  });

  const handleDetailsChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setSignUpDetails((prev) => ({ ...prev, [name]: value }));

    if (name === "email") {
      setErrors((prev) => ({
        ...prev,
        email:
          value && !isValidEmail(value) ? "Invalid email format" : undefined,
      }));
    } else if (name === "password") {
      setErrors((prev) => ({
        ...prev,
        password:
          !value || value.length < 6
            ? "Password should be at least 6 characters"
            : undefined,
      }));
    }
  };

  const handleSignUp = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setSignUpError({
      error: false,
      message: undefined,
    });

    try {
      const response = await fetch(getApiUrl("/auth/user/register"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(signUpDetails),
      });

      const data = await response.json();
      devLogger.log("🚀 ~ handleSignUp ~ data:", data);

      if (!response.ok || data.error) {
        setSignUpError({
          error: true,
          message: data.message || "Registration failed. Please try again.",
        });
      } else {
        navigate("/login");
      }
    } catch (error) {
      setSignUpError({
        error: true,
        message: "An error occurred. Please try again later.",
      });

      devLogger.error("Registration error:", error);
    } finally {
      setLoading(false);
    }
  };

  return {
    signUpDetails,
    errors,
    loading,
    signUpError,
    setSignUpError,
    handleDetailsChange,
    handleSignUp,
    isFormValid: isFormValid(signUpDetails, errors),
  };
};

type SignUpDetailsProps = {
  email: string | undefined;
  password: string | undefined;
};

type SignUpError = {
  error: boolean;
  message?: string;
};

interface UseUserSignUpReturn {
  loading: boolean;
  errors: { email?: string; password?: string };
  signUpError: SignUpError;
  signUpDetails: SignUpDetailsProps;
  isFormValid: boolean;
  setSignUpError: (signUpError: SignUpError) => void;
  handleDetailsChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleSignUp: (e: FormEvent<HTMLFormElement>) => Promise<void>;
}
